/**
 * Indicador de bala: el valor contra el rango que se considera sano.
 *
 * Sustituye a una columna de números con un «ideal» escrito al lado en prosa,
 * donde comparar exige leer dos cifras y hacer la resta de cabeza.
 *
 * El color solo aparece cuando hay un umbral FORMAL que cumplir. Un indicador
 * que solo informa —el capital de trabajo, por ejemplo— va en tinta neutra: no
 * existe un valor «bueno» de capital de trabajo, y pintarlo de verde afirmaría
 * un criterio que la contabilidad no ha dado. Y cuando hay color, hay también
 * palabra: el estado se escribe.
 */

export interface Bala {
  id: string;
  label: string;
  /** Ya formateado por quien conoce el formato del indicador. */
  texto: string;
  /** 0–1; cuánto del riel ocupa. `null` cuando no hay valor. */
  proporcion: number | null;
  /** El rango aprobado, tal como lo escribió Contabilidad. */
  ideal: string | null;
  /** Solo si existe un umbral formal. */
  estado?: "cumple" | "roza" | "incumple";
}

const PALABRA = { cumple: "Cumple", roza: "En el límite", incumple: "Fuera de rango" } as const;

export function Balas({ indicadores }: { indicadores: Bala[] }) {
  return (
    <div className="viz-balas">
      {indicadores.map((b) => (
        <div className="viz-bala" key={b.id}>
          <div className="viz-bala-cabeza">
            <span className="viz-bala-rotulo">{b.label}</span>
            <span className="viz-bala-valor num">{b.texto}</span>
          </div>
          <div className="viz-riel">
            {b.proporcion !== null && (
              <span
                className={`viz-barra ${b.estado ?? "neutro"}`}
                // Sin mínimo visual: un 2 % de riel para un indicador que vale 0
                // dibuja algo donde no hay nada, y en una pantalla de
                // indicadores eso es exactamente lo que no puede pasar.
                style={{ width: `${Math.min(100, Math.max(0, b.proporcion * 100))}%` }}
              />
            )}
          </div>
          <div className="viz-bala-pie small muted">
            {b.estado && <strong>{PALABRA[b.estado]}. </strong>}
            {b.ideal ? `Ideal ${b.ideal}` : "Sin rango definido"}
          </div>
        </div>
      ))}
    </div>
  );
}
